import React, { useEffect, useMemo, useState } from 'react';
import {
	CaretDown,
	CaretUp,
	CheckCircle,
	ListBullets,
	Stack,
	XCircle
} from '@phosphor-icons/react';
import ImageGallery from '../../../forms/components/ImageGallery.jsx';
import { taskService } from '../../../../services/tasks/taskService.js';

const TYPE_META = {
	general: {
		label: 'General',
		borderClass: 'border-l-sky-400'
	},
	finalizacion: {
		label: 'Finalización',
		borderClass: 'border-l-emerald-400'
	},
	cancelacion: {
		label: 'Cancelación',
		borderClass: 'border-l-rose-500'
	}
};

const TYPE_ORDER = {
	general: 0,
	finalizacion: 1,
	cancelacion: 2
};

const STATUS_META = {
	completado: {
		label: 'completado',
		className: 'bg-emerald-900/40 text-emerald-400 border border-emerald-600/50',
		icon: CheckCircle
	},
	cancelado: {
		label: 'cancelado',
		className: 'bg-rose-900/40 text-rose-400 border border-rose-600/50',
		icon: XCircle
	}
};

const formatDate = (dateValue) => {
	if (!dateValue) {
		return 'Sin fecha';
	}

	const parsedDate = new Date(dateValue);
	if (Number.isNaN(parsedDate.getTime())) {
		return 'Sin fecha';
	}

	return parsedDate.toLocaleDateString('es-AR', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric'
	});
};

const getFormType = (formResponse) => {
	const rawType = formResponse?.form?.type || formResponse?.formType || '';
	return String(rawType).toLowerCase();
};

const getFormTitle = (formResponse) => {
	const typeKey = getFormType(formResponse);
	const typeLabel = TYPE_META[typeKey]?.label || 'Formulario';

	return formResponse?.form?.name || `Formulario ${typeLabel}`;
};

const getStatusMeta = (statusValue) => {
	const normalizedStatus = String(statusValue || '').toLowerCase();
	return STATUS_META[normalizedStatus] || {
		label: normalizedStatus || 'pendiente',
		className: 'bg-surface-2/50 text-content border border-line',
		icon: CheckCircle
	};
};

const getFieldDefinition = (formResponse, key) => {
	const formFields = formResponse?.form?.fields;
	if (!Array.isArray(formFields)) {
		return null;
	}

	return formFields.find((field) => field.name === key || field.id === key);
};

const formatValue = (value) => {
	if (Array.isArray(value)) {
		if (value.length === 0) {
			return '-';
		}

		return value
			.map((item) => {
				if (typeof item === 'object' && item !== null) {
					return item.originalname || item.filename || JSON.stringify(item);
				}

				return String(item);
			})
			.join(', ');
	}

	if (value && typeof value === 'object') {
		return JSON.stringify(value);
	}

	if (value === undefined || value === null || value === '') {
		return '-';
	}

	return String(value);
};

const toDisplayLabel = (key) => {
	const normalized = String(key)
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		.replace(/[_-]/g, ' ')
		.trim();

	if (!normalized) {
		return 'Campo';
	}

	return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

function FormsTab({ taskId }) {
	const [forms, setForms] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [viewMode, setViewMode] = useState('list');
	const [expandedIds, setExpandedIds] = useState([]);

	useEffect(() => {
		if (!taskId) {
			setForms([]);
			setLoading(false);
			return;
		}

		let cancelled = false;

		const fetchFormResponses = async () => {
			try {
				setLoading(true);
				setError(null);

				const responses = await taskService.getTaskFormsResponses(taskId);

				if (cancelled) {
					return;
				}

				setForms(responses);
				setExpandedIds([]);
			} catch (fetchError) {
				if (cancelled) {
					return;
				}

				setError('No se pudieron cargar los formularios de la tarea.');
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		fetchFormResponses();

		return () => {
			cancelled = true;
		};
	}, [taskId]);

	const orderedForms = useMemo(() => {
		return [...forms].sort((left, right) => {
			const leftType = getFormType(left);
			const rightType = getFormType(right);

			const leftOrder = TYPE_ORDER[leftType] ?? 99;
			const rightOrder = TYPE_ORDER[rightType] ?? 99;

			if (leftOrder !== rightOrder) {
				return leftOrder - rightOrder;
			}

			const leftDate = new Date(left?.createdAt || 0).getTime();
			const rightDate = new Date(right?.createdAt || 0).getTime();
			return leftDate - rightDate;
		});
	}, [forms]);

	const isAllExpanded = orderedForms.length > 0 && expandedIds.length === orderedForms.length;

	const toggleForm = (formId) => {
		if (viewMode === 'list') {
			setExpandedIds((current) => (current[0] === formId ? [] : [formId]));
			return;
		}

		setExpandedIds((current) => (
			current.includes(formId)
				? current.filter((item) => item !== formId)
				: [...current, formId]
		));
	};

	const toggleAllForms = () => {
		if (isAllExpanded) {
			setExpandedIds([]);
			return;
		}

		setExpandedIds(orderedForms.map((formResponse) => formResponse.id || `${getFormType(formResponse)}-${formResponse.createdAt}`));
	};

	if (loading) {
		return (
			<div className="space-y-4">
				{[1, 2, 3].map((item) => (
					<div key={item} className="animate-pulse rounded-2xl border border-line bg-bg/70 p-5">
						<div className="mb-3 h-5 w-44 rounded bg-surface-2" />
						<div className="h-4 w-24 rounded bg-surface-2" />
					</div>
				))}
			</div>
		);
	}

	if (error) {
		return (
			<div className="rounded-3xl border border-red-800/70 bg-red-950/20 p-8 text-center">
				<p className="text-lg font-semibold text-red-200">{error}</p>
			</div>
		);
	}

	if (orderedForms.length === 0) {
		return (
			<div className="rounded-3xl border border-line bg-surface/70 p-8 text-center text-content">
				No hay formularios asociados a esta tarea.
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="inline-flex items-center rounded-2xl border border-line bg-bg/70 p-1">
					<button
						type="button"
						onClick={() => {
							setViewMode('list');
							setExpandedIds((current) => (current.length > 0 ? [current[0]] : []));
						}}
						className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-amber text-on-amber shadow-lg shadow-amber/20' : 'text-content-muted hover:bg-surface hover:text-content'}`}
					>
						<ListBullets size={16} />
						Lista
					</button>
					<button
						type="button"
						onClick={() => {
							setViewMode('columns');
							setExpandedIds([]);
						}}
						className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${viewMode === 'columns' ? 'bg-amber text-on-amber shadow-lg shadow-amber/20' : 'text-content-muted hover:bg-surface hover:text-content'}`}
					>
						<Stack size={16} />
						Columnas
					</button>
				</div>

				{viewMode === 'columns' && (
					<button
						type="button"
						onClick={toggleAllForms}
						className="inline-flex items-center gap-2 self-start rounded-xl border border-line bg-bg/60 px-3 py-2 text-sm font-medium text-content transition-colors hover:bg-surface hover:text-content"
					>
						{isAllExpanded ? 'Contraer todo' : 'Expandir todo'}
					</button>
				)}
			</div>

			<div className={viewMode === 'columns' ? 'grid gap-4 xl:grid-cols-2 2xl:grid-cols-3' : 'space-y-3'}>
				{orderedForms.map((formResponse) => {
					const formId = formResponse.id || `${getFormType(formResponse)}-${formResponse.createdAt}`;
					const isExpanded = expandedIds.includes(formId);
					const typeKey = getFormType(formResponse);
					const typeMeta = TYPE_META[typeKey] || {
						label: 'Formulario',
						borderClass: 'border-l-gray-600'
					};
					const statusMeta = getStatusMeta(formResponse.status);
					const StatusIcon = statusMeta.icon;

					const details = [
						{ key: 'technician', label: 'Técnico', value: formResponse?.technician?.name || formResponse?.technician?.email },
						{ key: 'client', label: 'Cliente', value: formResponse?.task?.clientSnapshot?.name || formResponse?.task?.client?.name },
						{ key: 'formType', label: 'Tipo de Formulario', value: typeMeta.label },
						{ key: 'status', label: 'Estado', value: statusMeta.label.charAt(0).toUpperCase() + statusMeta.label.slice(1) },
						{ key: 'address', label: 'Dirección', value: formResponse?.task?.clientSnapshot?.address || formResponse?.task?.client?.address },
						{ key: 'phone', label: 'Teléfono', value: formResponse?.task?.clientSnapshot?.phone || formResponse?.task?.client?.phone }
					];

					const extraFields = formResponse?.formData && typeof formResponse.formData === 'object'
						? Object.entries(formResponse.formData)
							.filter(([key]) => !['tecnico', 'cliente', 'direccion', 'telefono', 'estado'].includes(String(key).toLowerCase()))
							.map(([key, value]) => {
								const fieldDefinition = getFieldDefinition(formResponse, key);

								return {
									key,
									label: toDisplayLabel(key),
									value,
									type: fieldDefinition?.type || 'text'
								};
							})
						: [];

					const fieldsToRender = [...details, ...extraFields]
						.filter((field) => field.value !== undefined && field.value !== null && field.value !== '');
					const galleryFormId = formResponse?.form?.id;
					const galleryTaskId = formResponse?.taskId;
					const galleryTechnicianEmail = formResponse?.technician?.email || formResponse?.technicianId;
					const hasImageGalleryContext = Boolean(galleryFormId && galleryTaskId && galleryTechnicianEmail);

					return (
						<div
							key={formId}
							className={`overflow-hidden rounded-2xl border border-line bg-bg/70 border-l-4 ${typeMeta.borderClass}`}
						>
							<button
								type="button"
								onClick={() => toggleForm(formId)}
								className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-surface/40"
							>
								<div className="flex min-w-0 items-start gap-3">
									<div className="min-w-0">
										<p className="truncate text-lg font-semibold text-content sm:text-xl">{getFormTitle(formResponse)}</p>
										<p className="mt-1 text-base text-content-muted sm:text-lg">{formatDate(formResponse.createdAt)}</p>
									</div>
								</div>

								<div className="ml-3 flex items-center gap-3">
									<span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${statusMeta.className}`}>
										<StatusIcon size={12} />
										{statusMeta.label}
									</span>
									{isExpanded ? (
										<CaretUp size={16} className="text-content-muted" />
									) : (
										<CaretDown size={16} className="text-content-muted" />
									)}
								</div>
							</button>

							{isExpanded && (
								<div className="border-t border-line px-5 py-4">
									<div className="space-y-3">
										{fieldsToRender.map((field) => (
													<div key={field.key}>
												<p className="mb-1 text-sm text-content-muted">{field.label}:</p>
												{field.type === 'image' && hasImageGalleryContext ? (
													<div className="rounded-lg border border-line bg-bg/50 p-3">
														<ImageGallery
															formId={galleryFormId}
															taskId={galleryTaskId}
															technicianEmail={galleryTechnicianEmail}
															fieldName={field.key}
															expectedTotalCount={Array.isArray(field.value) ? field.value.length : null}
														/>
													</div>
												) : (
													<div className="rounded-lg bg-surface/70 px-3 py-2 text-sm text-content">
														{formatValue(field.value)}
													</div>
												)}
											</div>
										))}
									</div>
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
}

export default FormsTab;